import { api, unwrap } from "@lumberset/api-client";
import { ago, CustomerRunStatus, ProjectStatus } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  EmptyState,
  Field,
  FocusModal,
  Heading,
  Input,
  SectionHeader,
  Select,
  Table,
  Tabs,
  toast,
} from "@lumberset/ui";
import { ArrowUpTray, Buildings, MagnifyingGlass, Plus } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { customersQuery, projectsQuery } from "../../../lib/queries";

type StatusFilter = "active" | "won" | "lost" | "archived";

export const Route = createFileRoute("/_app/projects/")({
  validateSearch: (search: Record<string, unknown>): { new?: true } =>
    search.new === true || search.new === "true" ? { new: true } : {},
  component: Projects,
});

function Projects() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { yard } = Route.useRouteContext();
  // A Builder has no Customers: it prices a takeoff with a Budget Estimate or an RFQ.
  const builder = yard.kind === "builder";
  const [status, setStatus] = useState<StatusFilter>("active");
  const [text, setText] = useState("");
  const q = useDeferredValue(text);

  const { data, isPending } = useQuery(projectsQuery({ status, q: q || undefined }));
  const setCreating = (open: boolean) =>
    navigate({ to: "/projects", search: { new: open ? true : undefined }, replace: true });

  return (
    <>
      <Crumbs trail={[{ label: "Projects" }]} />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Projects"
          description={`One project per job. Each holds its drawings, takeoffs and ${builder ? "RFQs" : "quotes"}.`}
          actions={
            <Button size="small" onClick={() => setCreating(true)}>
              <Plus /> New project
            </Button>
          }
        />

        <div className="flex flex-col gap-3 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
          <Tabs value={status} onValueChange={(value) => setStatus(value as StatusFilter)}>
            <Tabs.List>
              <Tabs.Trigger value="active">Active</Tabs.Trigger>
              <Tabs.Trigger value="won">Won</Tabs.Trigger>
              <Tabs.Trigger value="lost">Lost</Tabs.Trigger>
              <Tabs.Trigger value="archived">Archived</Tabs.Trigger>
            </Tabs.List>
          </Tabs>
          <div className="w-full sm:w-64">
            <Input
              type="search"
              placeholder="Search projects"
              prefix={<MagnifyingGlass />}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </div>
        </div>

        {data && data.projects.length === 0 ? (
          status === "active" && !q ? (
            <FirstProject builder={builder} onStart={() => setCreating(true)} />
          ) : (
            <EmptyState
              icon={<Buildings />}
              title={q ? "No projects match" : `No ${status} projects`}
              description={
                q
                  ? "Try a different name."
                  : "Mark a project from its page: the menu beside its status has Won, Lost and Archived."
              }
            />
          )
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Project</Table.HeaderCell>
                {builder ? null : (
                  <Table.HeaderCell className="max-md:hidden">Customer</Table.HeaderCell>
                )}
                <Table.HeaderCell>Takeoff</Table.HeaderCell>
                {builder ? null : (
                  <Table.HeaderCell className="max-lg:hidden">Quotes</Table.HeaderCell>
                )}
                <Table.HeaderCell className="text-right max-sm:hidden">Updated</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body className={isPending ? "opacity-60" : undefined}>
              {data?.projects.map((project) => (
                <Table.Row
                  key={project.id}
                  data-clickable="true"
                  onClick={() => navigate({ to: "/projects/$id", params: { id: project.id } })}
                >
                  <Table.Cell>
                    <div className="txt-compact-small-plus text-ui-fg-base">{project.name}</div>
                    {builder ? null : (
                      <div className="txt-compact-xsmall text-ui-fg-muted md:hidden">
                        {project.customerName ?? "No customer"}
                      </div>
                    )}
                  </Table.Cell>
                  {builder ? null : (
                    <Table.Cell className="max-md:hidden">{project.customerName ?? "-"}</Table.Cell>
                  )}
                  <Table.Cell>
                    {project.runStatus ? (
                      <CustomerRunStatus status={project.runStatus} />
                    ) : (
                      <ProjectStatus status={project.status === "active" ? null : project.status} />
                    )}
                    {!project.runStatus && project.status === "active" ? (
                      <span className="text-ui-fg-muted">No drawings yet</span>
                    ) : null}
                  </Table.Cell>
                  {builder ? null : (
                    <Table.Cell className="tabular max-lg:hidden">{project.quoteCount}</Table.Cell>
                  )}
                  <Table.Cell className="whitespace-nowrap text-right max-sm:hidden">
                    {ago(project.updatedAt)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <NewProject open={search.new === true} onOpenChange={setCreating} builder={builder} />
    </>
  );
}

/**
 * The projects page before the first project: what the first upload leads to,
 * what comes back and when, said once, with the one button that starts it.
 */
function FirstProject({ builder, onStart }: { builder: boolean; onStart: () => void }) {
  const steps: { title: string; detail: string }[] = [
    {
      title: "Create a project and drop in the drawings",
      detail:
        "One project per job. A PDF of the plan set is all it needs, any size, any number of sheets.",
    },
    {
      title: "We read it, and our Reviewers check it",
      detail:
        "Reading takes a few minutes. The check takes longer, so we email you the moment the takeoff is ready: every line with its arithmetic and the sheet it was read from. Your first one is free.",
    },
    builder
      ? {
          title: "Budget it, then ask yards for quotes",
          detail:
            "A budget at reference prices before any yard has quoted, and one send to the yards you choose, with their replies side by side.",
        }
      : {
          title: "Price it and send the quote",
          detail:
            "Lines are priced from your catalog; change any price, then send your customer a link and a PDF. You hear when they open it.",
        },
  ];

  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-ui-bg-base text-ui-fg-subtle shadow-elevation-card-rest">
        <ArrowUpTray />
      </div>
      <p className="txt-compact-medium-plus text-ui-fg-base">
        {builder
          ? "Your first takeoff starts with a project"
          : "Your first quote starts with a project"}
      </p>
      <p className="txt-small mt-1 max-w-sm text-balance text-ui-fg-subtle">
        From drawings to {builder ? "a budget and yards' quotes" : "a sent quote"} in three steps.
      </p>
      <ol className="mt-6 flex w-full max-w-lg flex-col gap-y-4 text-left">
        {steps.map((step, index) => (
          <li key={step.title} className="flex items-start gap-x-3">
            <span className="txt-compact-xsmall-plus mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-ui-bg-component text-ui-fg-subtle">
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="txt-compact-small-plus text-ui-fg-base">{step.title}</p>
              <p className="txt-small text-pretty text-ui-fg-subtle">{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-6">
        <Button size="large" onClick={onStart}>
          <Plus /> Create your first project
        </Button>
      </div>
    </div>
  );
}

const NO_CUSTOMER = "none";

function NewProject({
  open,
  onOpenChange,
  builder,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  builder: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: customers } = useQuery({ ...customersQuery(), enabled: open && !builder });
  const [customerId, setCustomerId] = useState(NO_CUSTOMER);

  const create = useMutation({
    mutationFn: async (form: FormData) => {
      let chosen: string | null = customerId === NO_CUSTOMER ? null : customerId;

      const newCustomer = String(form.get("customerName") ?? "").trim();
      if (!chosen && newCustomer) {
        const created = await unwrap(
          api.customers.$post({
            json: {
              name: newCustomer,
              email: String(form.get("customerEmail") ?? "").trim() || null,
            },
          }),
        );
        chosen = created.customer.id;
      }

      return unwrap(
        api.projects.$post({
          json: {
            name: String(form.get("name")).trim(),
            customerId: chosen,
            siteAddress: { line1: String(form.get("address") ?? "").trim() || undefined },
          },
        }),
      );
    },
    onSuccess: async ({ project }) => {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Project created. Upload the drawings next.");
      await navigate({ to: "/projects/$id", params: { id: project.id } });
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(new FormData(event.currentTarget));
  }

  return (
    <FocusModal open={open} onOpenChange={onOpenChange}>
      <FocusModal.Content>
        <form onSubmit={submit} className="flex h-full flex-col">
          <FocusModal.Header>
            <Button
              type="button"
              size="small"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="small" isLoading={create.isPending}>
              Create project
            </Button>
          </FocusModal.Header>

          <FocusModal.Body>
            <div className="mx-auto flex w-full max-w-[560px] flex-col gap-y-8 px-6 py-10 sm:py-16">
              <div>
                <FocusModal.Title asChild>
                  <Heading>New project</Heading>
                </FocusModal.Title>
                <FocusModal.Description className="txt-small mt-1 text-ui-fg-subtle">
                  A project is one job at one site. You will upload its drawings on the next screen.
                </FocusModal.Description>
              </div>

              <Field
                label="Project name"
                hint={
                  builder
                    ? "Most builders use the site address or their own job name."
                    : "Most yards use the site address or the builder's job name."
                }
              >
                {(control) => (
                  <Input
                    {...control}
                    name="name"
                    placeholder="1260 Winchester Way"
                    required
                    autoFocus
                    size="large"
                  />
                )}
              </Field>

              <Field label="Site address" optional>
                {(control) => (
                  <Input {...control} name="address" placeholder="Street, city" size="large" />
                )}
              </Field>

              {builder ? null : (
                <Field
                  label="Customer"
                  optional
                  hint="Who the quote will go to. You can also choose on the quote."
                >
                  {(control) => (
                    <Select value={customerId} onValueChange={setCustomerId}>
                      <Select.Trigger id={control.id} size="large">
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.Item value={NO_CUSTOMER}>New or no customer</Select.Item>
                        {customers?.customers.map((customer) => (
                          <Select.Item key={customer.id} value={customer.id}>
                            {customer.name}
                            {customer.company ? `, ${customer.company}` : ""}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select>
                  )}
                </Field>
              )}

              {!builder && customerId === NO_CUSTOMER ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="New customer name" optional>
                    {(control) => (
                      <Input
                        {...control}
                        name="customerName"
                        placeholder="Dana Builder"
                        size="large"
                      />
                    )}
                  </Field>
                  <Field label="Their email" optional>
                    {(control) => (
                      <Input
                        {...control}
                        name="customerEmail"
                        type="email"
                        placeholder="dana@example.com"
                        size="large"
                      />
                    )}
                  </Field>
                </div>
              ) : null}
            </div>
          </FocusModal.Body>
        </form>
      </FocusModal.Content>
    </FocusModal>
  );
}
